export class FelixChessBoardCaptions {
    constructor(options = {}) {
        this.rootEl = options.rootEl || null;
        this.topMetaEl = options.topMetaEl || null;
        this.navEl = options.navEl || null;
        this.context = options.context || 'default';

        this.white = null;
        this.black = null;
        this.meta = null;

        this._unsubscribePositionChange = null;
        this._unsubscribeCleared = null;

        this.renderShell();

        // Wire replay immediately if provided at construction time
        if (options.replay) {
            this.setReplay(options.replay);
        }
    }

    renderShell() {
        if (this.topMetaEl) {
            this.topMetaEl.innerHTML = `
                <div class="fc-boardViewer__event" data-role="eventLine" hidden></div>
                <div class="fc-boardViewer__player fc-boardViewer__player--black" data-role="playerBlack" hidden></div>
            `;
            this.topMetaEl.hidden = true;
        }

        const boardPaneEl = this.rootEl
            ? this.rootEl.querySelector('.fc-boardViewer__boardPane')
            : null;

        if (boardPaneEl) {
            const existingBlackClock = boardPaneEl.querySelector('[data-role="clockBlackBoard"]');
            if (!existingBlackClock) {
                boardPaneEl.insertAdjacentHTML(
                    'beforeend',
                    `<span class="fc-boardViewer__clock fc-boardViewer__clock--board fc-boardViewer__clock--blackBoard"
                        data-role="clockBlackBoard"
                        hidden></span>`
                );
            } else {
                existingBlackClock.hidden = true;
            }

            const existingWhite = boardPaneEl.querySelector('[data-role="playerWhite"]');
            if (!existingWhite) {
                boardPaneEl.insertAdjacentHTML(
                    'beforeend',
                    `<div class="fc-boardViewer__player fc-boardViewer__player--white" data-role="playerWhite" hidden></div>`
                );
            } else {
                existingWhite.hidden = true;
            }
        }
    }

    loadGameMeta({ white = null, black = null, meta = null } = {}) {
        this.white = white;
        this.black = black;
        this.meta = meta;
        console.log('[FelixChess][board-captions] received meta', meta);
        this.update();
    }

    setGameMeta({ white = null, black = null, meta = null } = {}) {
        this.loadGameMeta({ white, black, meta });
    }

    update() {
        const eventEl = this.topMetaEl
            ? this.topMetaEl.querySelector('[data-role="eventLine"]')
            : null;

        const blackEl = this.topMetaEl
            ? this.topMetaEl.querySelector('[data-role="playerBlack"]')
            : null;

        const whiteEl = this.rootEl
            ? this.rootEl.querySelector('.fc-boardViewer__boardPane [data-role="playerWhite"]')
            : null;

        // formatEventLine now returns an HTML string (may include a game-type chip)
        const eventHtml = this.formatEventLine(this.meta);
        const blackHtml = this.renderPlayerHtml(this.black);
        const whiteHtml = this.renderPlayerHtml(this.white);

        if (eventEl) {
            // Use innerHTML so the game-type chip <span> is rendered correctly
            eventEl.innerHTML = eventHtml;
            eventEl.hidden = !eventHtml;
        }

        if (blackEl) {
            blackEl.innerHTML = blackHtml;
            blackEl.hidden = !blackHtml;
        }

        if (whiteEl) {
            whiteEl.innerHTML = whiteHtml;
            whiteEl.hidden = !whiteHtml;
        }

        if (this.topMetaEl) {
            this.topMetaEl.hidden = !eventHtml && !blackHtml;
        }
    }

    clear() {
        this.white = null;
        this.black = null;
        this.meta = null;

        const eventEl = this.topMetaEl
            ? this.topMetaEl.querySelector('[data-role="eventLine"]')
            : null;

        const blackEl = this.topMetaEl
            ? this.topMetaEl.querySelector('[data-role="playerBlack"]')
            : null;

        const whiteEl = this.rootEl
            ? this.rootEl.querySelector('.fc-boardViewer__boardPane [data-role="playerWhite"]')
            : null;

        if (eventEl) {
            eventEl.innerHTML = '';
            eventEl.hidden = true;
        }

        if (blackEl) {
            blackEl.innerHTML = '';
            blackEl.hidden = true;
        }

        if (whiteEl) {
            whiteEl.innerHTML = '';
            whiteEl.hidden = true;
        }

        if (this.topMetaEl) {
            this.topMetaEl.hidden = true;
        }
    }

    destroy() {
        this.clear();
        if (this._unsubscribePositionChange) {
            this._unsubscribePositionChange();
            this._unsubscribePositionChange = null;
        }
        if (this._unsubscribeCleared) {
            this._unsubscribeCleared();
            this._unsubscribeCleared = null;
        }
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    }

    renderFlag(player) {
        if (!player || !player.flagKey) {
            return '';
        }

        const federationLabel = this.escapeHtml(
            player.federationDisplayName || player.federation || 'FIDE'
        );

        if (player.flagKey === 'fide') {
            return `<img
                class="fc-flag fc-flag--fide"
                src="/etc/vendor-libraries/felixchess-versioned-library/versions/0.0.1/assets/fide.svg"
                alt="${federationLabel}"
                title="${federationLabel}">`;
        }

        return `<span
            class="fi fi-${this.escapeHtml(player.flagKey)} fc-flagIcon"
            aria-hidden="true"
            title="${federationLabel}"></span>`;
    }

    renderTitle(player) {
        if (!player || !player.title) {
            return '';
        }

        return `<span class="fc-boardViewer__title">${this.escapeHtml(player.title)}</span>`;
    }

    renderPlayerHtml(player) {
        if (!player || !player.name) {
            return '';
        }

        const elo = player.elo;
        const hasElo = elo !== undefined && elo !== null && elo !== '';
        const rating = hasElo
            ? ` <span class="fc-boardViewer__rating">(${this.escapeHtml(elo)})</span>`
            : '';

        return `
            ${this.renderFlag(player)}
            ${this.renderTitle(player)}
            <span class="fc-boardViewer__name">${this.escapeHtml(player.name)}</span>${rating}
            <span class="fc-boardViewer__clock" data-role="clock" hidden></span>
        `.trim();
    }

    // ─── Clock display ──────────────────────────────────────────────────────

    /**
     * Wire this captions instance to a replay controller so clocks update
     * automatically on every position change. Safe to call multiple times —
     * the previous subscription is cleaned up first.
     * @param {FelixChessReplayController} replay
     */
    setReplay(replay) {
        // Unsubscribe previous listeners
        if (this._unsubscribePositionChange) {
            this._unsubscribePositionChange();
            this._unsubscribePositionChange = null;
        }
        if (this._unsubscribeCleared) {
            this._unsubscribeCleared();
            this._unsubscribeCleared = null;
        }

        if (!replay) return;

        this._unsubscribePositionChange = replay.on('positionchange', (state) => {
            this.updateClocks(state);
        });

        this._unsubscribeCleared = replay.on('cleared', () => {
            this._hideClock('black');
            this._hideClock('white');
        });
    }

    /**
     * Compute the correct clock values for both players at the current
     * position index and update the DOM slots.
     *
     * Clock index semantics (parallel to moves[]):
     *   clocks[0] = remaining time after ply 1 (White's first move)
     *   clocks[1] = remaining time after ply 2 (Black's first move)
     *   …
     * Odd ply numbers → White moved; even ply numbers → Black moved.
     *
     * @param {object} state - value from replay.getState()
     */
    updateClocks(state) {
        const clocks = Array.isArray(state?.clocks) ? state.clocks : [];
        const currentIndex = state?.currentIndex ?? 0;
        const initialClocks = state?.initialClocks || state?.meta?.initialClocks || {};
        console.log('[FelixChess][board-captions] Current ply', currentIndex, 'clocks', clocks, 'initialClocks', initialClocks);

        // If no clock data at all, hide both and return
        const hasAnyClocks = clocks.some(c => c != null) || !!initialClocks.white || !!initialClocks.black;
        if (!hasAnyClocks) {
            this._hideClock('black');
            this._hideClock('white');
            return;
        }

        // Walk up to currentIndex, collecting the latest clock for each side
        let whiteClock = initialClocks.white || null;
        let blackClock = initialClocks.black || null;
        let previousWhiteClock = this._parseClockSeconds(whiteClock);
        let previousBlackClock = this._parseClockSeconds(blackClock);
        let whiteSpent = null;
        let blackSpent = null;
        const incrementSeconds = this._parseIncrementSeconds(
            state?.meta?.timeControl || state?.meta?.TimeControl || ''
        );

        for (let ply = 1; ply <= currentIndex; ply++) {
            const clk = clocks[ply - 1];   // clocks[] is 0-indexed vs 1-indexed plies
            if (clk == null) continue;
            const currentClockSeconds = this._parseClockSeconds(clk);
            if (ply % 2 === 1) {
                if (previousWhiteClock != null && currentClockSeconds != null) {
                    whiteSpent = Math.max(0, previousWhiteClock + incrementSeconds - currentClockSeconds);
                }
                whiteClock = clk;   // odd ply  → White just moved
                previousWhiteClock = currentClockSeconds;
            } else {
                if (previousBlackClock != null && currentClockSeconds != null) {
                    blackSpent = Math.max(0, previousBlackClock + incrementSeconds - currentClockSeconds);
                }
                blackClock = clk;    // even ply → Black just moved
                previousBlackClock = currentClockSeconds;
            }
        }

        // Side to move at currentIndex:
        // position 0, 2, 4 … → White to move; position 1, 3, 5 … → Black to move
        const whiteToMove = (currentIndex % 2 === 0);
        console.log('[FelixChess][board-captions] Clock selected for current ply', {
            currentIndex,
            whiteClock,
            blackClock,
            whiteSpent,
            blackSpent,
            whiteToMove
        });

        this._showClock('white', whiteClock, whiteToMove, this._formatSpent(whiteSpent));
        this._showClock('black', blackClock, !whiteToMove, this._formatSpent(blackSpent));
    }

    /**
     * Render a single clock slot.
     * @param {'white'|'black'} side
     * @param {string|null}     timeStr  e.g. "0:25:18"
     * @param {boolean}         isActive true when it is this player's turn to move
     * @param {string}          spentStr e.g. "0:38"
     */
    _showClock(side, timeStr, isActive, spentStr = '') {
        const el = this._getClockEl(side);
        console.log('[FelixChess][board-captions] DOM update target', side, el, timeStr);
        if (!el) return;

        const display = timeStr ? this._formatClock(timeStr) : '--:--';
        el.textContent = '';

        const timeEl = document.createElement('span');
        timeEl.className = 'fc-boardViewer__clockTime';
        timeEl.textContent = display;
        el.appendChild(timeEl);

        if (spentStr) {
            const spentEl = document.createElement('span');
            spentEl.className = 'fc-boardViewer__timeSpent';
            spentEl.title = 'Time spent on latest move';
            spentEl.textContent = `(${spentStr})`;
            el.appendChild(spentEl);
        }

        el.hidden = false;
        el.classList.toggle('fc-boardViewer__clock--active',   isActive);
        el.classList.toggle('fc-boardViewer__clock--inactive', !isActive);
    }

    _hideClock(side) {
        const el = this._getClockEl(side);
        if (!el) return;
        el.hidden = true;
        el.classList.remove('fc-boardViewer__clock--active', 'fc-boardViewer__clock--inactive');

        if (side === 'black') {
            const rowClock = this.topMetaEl
                ? this.topMetaEl.querySelector('[data-role="playerBlack"] [data-role="clock"]')
                : null;
            if (rowClock && rowClock !== el) {
                rowClock.hidden = true;
                rowClock.classList.remove('fc-boardViewer__clock--active', 'fc-boardViewer__clock--inactive');
            }
        }
    }

    _getClockEl(side) {
        if (side === 'black') {
            const boardClock = this.rootEl
                ? this.rootEl.querySelector('.fc-boardViewer__boardPane [data-role="clockBlackBoard"]')
                : null;
            if (boardClock) {
                return boardClock;
            }

            return this.topMetaEl
                ? this.topMetaEl.querySelector('[data-role="playerBlack"] [data-role="clock"]')
                : null;
        }
        return this.rootEl
            ? this.rootEl.querySelector('[data-role="playerWhite"] [data-role="clock"]')
            : null;
    }

    /**
     * Format a PGN clock string ("H:MM:SS") for display.
     * - If hours == 0 → "MM:SS"
     * - If hours > 0  → "H:MM:SS"
     * @param {string} timeStr e.g. "0:25:18" or "1:03:07"
     * @returns {string}
     */
    _formatClock(timeStr) {
        if (!timeStr) return '--:--';
        const parts = timeStr.split(':');
        if (parts.length < 3) return timeStr; // unexpected format, pass through
        const h  = parseInt(parts[0], 10);
        const mm = parts[1].padStart(2, '0');
        const ss = parts[2].padStart(2, '0');
        return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
    }

    _parseClockSeconds(timeStr) {
        if (!timeStr || typeof timeStr !== 'string') {
            return null;
        }

        const parts = timeStr.trim().split(':').map(part => Number.parseInt(part, 10));
        if (parts.some(part => Number.isNaN(part))) {
            return null;
        }

        if (parts.length === 3) {
            return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
        }
        if (parts.length === 2) {
            return (parts[0] * 60) + parts[1];
        }
        if (parts.length === 1) {
            return parts[0];
        }
        return null;
    }

    _parseIncrementSeconds(timeControl) {
        if (!timeControl || typeof timeControl !== 'string') {
            return 0;
        }

        const value = timeControl.trim();
        const plusMatch = /\+\s*(\d+)/.exec(value);
        if (plusMatch) {
            return Number.parseInt(plusMatch[1], 10) || 0;
        }

        const narrativeMatch = /\+\s*(\d+)\s*(?:sec|second)/i.exec(value);
        if (narrativeMatch) {
            return Number.parseInt(narrativeMatch[1], 10) || 0;
        }

        return 0;
    }

    _formatSpent(spentSeconds) {
        if (spentSeconds == null) {
            return '';
        }

        const total = Math.max(0, Math.floor(spentSeconds));
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        const ss = String(seconds).padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
        }
        return `${minutes}:${ss}`;
    }

    /**
     * Builds the event-line HTML string.
     *
     * The text portion follows the existing "Event - Year - Round N" pattern.
     * When the meta object carries a time category (meta.timeCategory),
     * it is appended as a styled chip:
     *
     *   The American Cup - 2025 - Round 1.6  <span class="fc-boardViewer__gameType ...">Rapid</span>
     *
     * Recognised time-category values are mapped to BEM modifier classes so each
     * category can be coloured independently via CSS:
     *
     *   bullet  → fc-boardViewer__gameType--bullet   (red)
     *   blitz   → fc-boardViewer__gameType--blitz    (orange  — matches the screenshot)
     *   rapid   → fc-boardViewer__gameType--rapid    (amber)
     *   classical / standard → fc-boardViewer__gameType--classical (blue)
     *
     * Returns an HTML string (may be empty).
     */
    formatEventLine(meta) {
        if (!meta) {
            return '';
        }

        const event = meta.event || '';
        const date  = meta.date  || '';
        const round = meta.round || '';

        // Use only canonical timeCategory. gameType is a variant/classification value
        // such as "standard" and must not replace pgn:timeCategory.
        const rawGameType = meta.timeCategory || '';

        let year = '';
        const match = /^(\d{4})/.exec(date);
        if (match) {
            year = match[1];
        }

        const parts = [];
        if (event) parts.push(this.escapeHtml(event));
        if (year)  parts.push(this.escapeHtml(year));
        if (round) parts.push(`Round ${this.escapeHtml(round)}`);

        const textPart = parts.join(' - ');

        if (!textPart && !rawGameType) {
            return '';
        }

        let gameTypeHtml = '';
        if (rawGameType) {
            const normalised = rawGameType.trim().toLowerCase();

            // Map to a BEM modifier so CSS can target each category independently
            let modifier = '';
            if (normalised === 'bullet')                         modifier = 'bullet';
            else if (normalised === 'blitz')                     modifier = 'blitz';
            else if (normalised === 'rapid')                     modifier = 'rapid';
            else if (normalised === 'classical'
                || normalised === 'classic'
                || normalised === 'standard'
                || normalised === 'correspondence')            modifier = 'classical';

            const modClass = modifier
                ? ` fc-boardViewer__gameType--${modifier}`
                : '';

            // Display the label with its original casing preserved
            const label = this.escapeHtml(
                rawGameType.charAt(0).toUpperCase() + rawGameType.slice(1).toLowerCase()
            );

            gameTypeHtml = `<span class="fc-boardViewer__gameType${modClass}" aria-label="Game type: ${label}">${label}</span>`;
        }

        if (!textPart) {
            return gameTypeHtml;
        }

        return gameTypeHtml
            ? `<span class="fc-boardViewer__eventText">${textPart}</span>${gameTypeHtml}`
            : `<span class="fc-boardViewer__eventText">${textPart}</span>`;
    }
}
